import { useEffect, useState } from "react";
import { Table, ConfigProvider, Button, Input, Select, Card, Pagination, Modal, Spin } from "antd";
import { FileTextOutlined, SearchOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import ResumeDatabaseModal from "./Modal/ResumeDatabaseModal";
import { useResumeDatabase } from "../../app/hooks/useResumeDatabase";
import Debounce from "../../app/utils/debounce";
import { PaginatedResult } from "../../app/utils/pagination";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";

const debouncer = new Debounce();

const { Option } = Select;

const ResumeDatabaseList = () => {
  const [paginatedResult, setPaginatedResult] = useState(new PaginatedResult());
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedApplicantIndex, setSelectedApplicantIndex] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const location = useLocation();
  const { departmentId, departmentName } = location.state || {};
  const positionOptions = Array.from(new Set((paginatedResult.data || []).map((job) => job.position).filter(Boolean)));

  const pageSize = 10;

  const { getResumeDatabaseByDepartmentId } = useResumeDatabase({
    id: departmentId,
    pageSize: pageSize,
  });

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    debouncer.debounce({
      debounce: () => {
        setCurrentPage(1);
        setDebouncedSearch(value);
      },
      debounceTimeout: 800,
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const result = await getResumeDatabaseByDepartmentId(departmentId, currentPage, pageSize, debouncedSearch || selectedPosition);
      setPaginatedResult(result);
    } catch (error) {
      const errorMessage = error?.response?.data?.details || error?.response?.data?.title || error?.message || "Department not found.";
      console.log(errorMessage);
      setPaginatedResult(new PaginatedResult());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [departmentId, currentPage, pageSize, debouncedSearch, selectedPosition]);

  const handleView = (index) => {
    setSelectedApplicantIndex(index);
    setIsModalVisible(true);
  };

  const columns = [
    { title: "UPLOAD DATE", dataIndex: "uploadDate", key: "uploadDatedate", align: "center", render: (date) => <p>{dayjs(date).format("MMM DD, YYYY")}</p> },
    { title: "NAME", dataIndex: "fullName", key: "fullName", align: "center" },
    { title: "POSITION", dataIndex: "position", key: "position", align: "center" },
    {
      title: "",
      key: "action",
      align: "center",
      render: (_, __, index) => (
        <Button
          type="primary"
          className="mx-auto flex items-center hover:opacity-80"
          style={{ backgroundColor: "#F97316", borderColor: "#F97316", fontWeight: "600", borderRadius: "9999px" }}
          onClick={() => handleView(index)}
        >
          <FileTextOutlined /> VIEW
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card className="rounded-2xl px-4 md:px-16 py-8 shadow-2xl w-full overflow-hidden">
        <div className="pb-6">
          <p className="font-medium text-3xl m-0">{departmentName}</p>
        </div>
        <div className="flex justify-between flex-col md:flex-row md:gap-8 items-center mb-6">
          <div className="flex flex-col md:flex-row gap-8 ">
            <Input
              size="large"
              placeholder="Search here"
              value={searchText}
              onChange={handleSearchChange}
              prefix={<SearchOutlined style={{ fontSize: "20px", color: "#666" }} />}
            />
            <Select
              size="large"
              placeholder="Search position"
              value={selectedPosition}
              allowClear
              className="w-full md:w-80"
              onChange={(value) => {
                setSelectedPosition(value);
                setCurrentPage(1);
              }}
            >
              {positionOptions.map((position) => (
                <Option key={position} value={position}>
                  {position}
                </Option>
              ))}
            </Select>
          </div>
          <h2 className="text-2xl font-medium m-0">
            {paginatedResult.totalItems} {paginatedResult.totalItems <= 1 ? "Resume" : "Resumes"}{" "}
          </h2>
        </div>

        <ConfigProvider
          theme={{
            components: {
              Table: { headerBg: "#364042", headerColor: "white" },
            },
          }}
        >
          {isLoading ? (
            <div className="col-span-3 flex justify-center py-20">
              <Spin tip="Fetching Job Openings..." />
            </div>
          ) : (
            <Table rowKey="id" columns={columns} dataSource={paginatedResult.data} pagination={false} />
          )}
          <div className="flex justify-center mt-8">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              className="job-search-pagination"
              total={paginatedResult.totalItems}
              onChange={(page) => {
                setCurrentPage(page);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              showSizeChanger={false}
            />
          </div>
        </ConfigProvider>
      </Card>

      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={1200}
        centered
        destroyOnClose
        styles={{ body: { padding: 0 } }}
      >
        <ResumeDatabaseModal
          applicants={paginatedResult.data}
          currentIndex={selectedApplicantIndex}
          onNext={() => setSelectedApplicantIndex((prev) => (prev + 1) % paginatedResult.data.length)}
          onPrevious={() => setSelectedApplicantIndex((prev) => (prev - 1 + paginatedResult.data.length) % paginatedResult.data.length)}
          onClose={() => {
            setIsModalVisible(false);
            setSelectedApplicantIndex(null);
          }}
        />
      </Modal>
    </>
  );
};

export default observer(ResumeDatabaseList);
